import type { SceneObject } from "../modeling/types.ts";

export interface SparseVertexPatch {
  /** Shared vertex indices touched by the latest preview tick. */
  indices: Uint32Array;
  /** Absolute object-local xyz positions, parallel to `indices`. */
  positions: Float32Array;
}

export type EditSessionGeometryPatch = Partial<
  Pick<
    SceneObject,
    | "vertices"
    | "topology"
    | "indices"
    | "loopNormals"
    | "uvs"
    | "skinRadii"
    | "baseVertices"
  >
>;

export interface EditSessionTransientPatch {
  /** Exact object-local vertices touched by the latest live preview tick. */
  dirtyVertices?: Uint32Array | null;
  /** Sparse absolute positions for `dirtyVertices`, used during live preview. */
  vertexPatch?: SparseVertexPatch | null;
}

export type EditSessionPatch = EditSessionGeometryPatch &
  EditSessionTransientPatch;

export interface ActiveEditSession extends EditSessionGeometryPatch {
  objectId: string;
  /** Transient drag-time metadata. Never promoted into committed sceneObjects. */
  dirtyVertices: Uint32Array | null;
  /** Transient sparse vertex positions. Never promoted into committed sceneObjects. */
  vertexPatch: SparseVertexPatch | null;
}

export function patchActiveEditSession(
  current: ActiveEditSession | null,
  objectId: string,
  patch: EditSessionPatch,
): ActiveEditSession {
  const dirtyVertices = Object.hasOwn(patch, "dirtyVertices")
    ? (patch.dirtyVertices ?? null)
    : null;
  const vertexPatch = Object.hasOwn(patch, "vertexPatch")
    ? (patch.vertexPatch ?? null)
    : null;
  const next =
    current?.objectId === objectId
      ? { ...current, ...patch, objectId }
      : { objectId, ...patch };
  return { ...next, dirtyVertices, vertexPatch };
}

export function resolveSceneObjectEditSession(
  obj: SceneObject,
  session: ActiveEditSession | null | undefined,
): SceneObject {
  if (!session || session.objectId !== obj.id) return obj;
  const {
    objectId: _objectId,
    dirtyVertices: _dirtyVertices,
    vertexPatch: _vertexPatch,
    ...patch
  } = session;
  return { ...obj, ...patch };
}

export function materializeSparseVertexPatch(
  baseVertices: Float32Array,
  sparse: SparseVertexPatch,
): Float32Array {
  const vertices = new Float32Array(baseVertices);
  for (let i = 0; i < sparse.indices.length; i++) {
    const vi = sparse.indices[i]!;
    const src = i * 3;
    const dst = vi * 3;
    vertices[dst] = sparse.positions[src]!;
    vertices[dst + 1] = sparse.positions[src + 1]!;
    vertices[dst + 2] = sparse.positions[src + 2]!;
  }
  return vertices;
}

export function materializeEditSessionPatch(
  obj: SceneObject,
  session: ActiveEditSession,
): EditSessionGeometryPatch {
  const patch = extractEditSessionPatch(session);
  if (!patch.vertices && session.vertexPatch) {
    patch.vertices = materializeSparseVertexPatch(
      obj.vertices,
      session.vertexPatch,
    );
  }
  return patch;
}

export function extractEditSessionPatch(
  session: ActiveEditSession,
): EditSessionGeometryPatch {
  return {
    ...(session.vertices ? { vertices: session.vertices } : {}),
    ...(session.topology ? { topology: session.topology } : {}),
    ...(session.indices ? { indices: session.indices } : {}),
    ...(session.loopNormals ? { loopNormals: session.loopNormals } : {}),
    ...(session.uvs ? { uvs: session.uvs } : {}),
    ...(session.skinRadii ? { skinRadii: session.skinRadii } : {}),
    ...(session.baseVertices ? { baseVertices: session.baseVertices } : {}),
  };
}
